import { useState, useEffect } from "react";
import { Building2, MapPin, Briefcase, Users, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useBusiness } from "@/contexts/BusinessContext";
import { useTheme } from "@/contexts/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_LIGHT_URL = "/logo-light.png";
const LOGO_DARK_URL = "/logo-dark.png";

const BusinessSelectionModal = ({ onComplete, user }) => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [canSwitchBusiness, setCanSwitchBusiness] = useState(false);
  const { selectBusiness } = useBusiness();
  const { theme } = useTheme();

  const currentLogo = theme === "dark" ? LOGO_DARK_URL : LOGO_LIGHT_URL;
  const isSuperAdmin = user?.role === "super_admin";

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/auth/my-businesses`, {
        credentials: "include",
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch businesses");
      }
      
      const data = await response.json();
      setBusinesses(data.businesses || []);
      setCanSwitchBusiness(data.can_switch_business || false);
      
      // Auto-select business: skip the selection page entirely
      if (data.businesses?.length >= 1) {
        // Find user's own business first, fallback to first in list
        const userBiz = user?.business_id ? data.businesses.find(b => b.business_id === user.business_id) : null;
        handleSelectBusiness(userBiz || data.businesses[0]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBusiness = async (business) => {
    setSelecting(true);
    selectBusiness(business);
    // Small delay for visual feedback
    setTimeout(() => {
      onComplete(business);
    }, 300);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-indigo-50/30 to-pink-50/30 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center z-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-3" />
          <p className="text-slate-600 dark:text-slate-400">Loading your businesses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-indigo-50/30 to-pink-50/30 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center z-50">
        <div className="text-center p-6">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Error</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-4">{error}</p>
          <Button onClick={fetchBusinesses} className="bg-indigo-600 hover:bg-indigo-700">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (businesses.length === 0) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-indigo-50/30 to-pink-50/30 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center z-50 p-4">
        <div className="text-center max-w-md">
          <img
            src={currentLogo}
            alt="Dnexus Media"
            className="h-12 sm:h-14 mx-auto mb-6 object-contain"
          />
          <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-10 h-10 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            No Business Assigned
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            {isSuperAdmin 
              ? "No businesses found in the system. Go to Settings to create your first business."
              : "You haven't been assigned to any business yet. Please contact your administrator to get access."
            }
          </p>
          <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-4 text-sm text-slate-600 dark:text-slate-400">
            <p className="mb-2"><strong>User:</strong> {user?.name || user?.email}</p>
            <p><strong>Role:</strong> {user?.role === "super_admin" ? "Super Admin" : user?.role}</p>
          </div>
        </div>
      </div>
    );
  }

  // Only show selection UI for Super Admin with multiple businesses
  // Non-super-admin users are auto-redirected above
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-indigo-50/30 to-pink-50/30 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="w-full max-w-2xl py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src={currentLogo}
            alt="Dnexus Media"
            className="h-12 sm:h-14 mx-auto mb-6 object-contain"
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
            Select Your Business
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {isSuperAdmin ? "Choose a business to manage" : "Choose a business to continue"}
          </p>
        </div>

        {/* Business Cards Grid */}
        <div className="grid gap-4 sm:grid-cols-2">
          {businesses.map((business) => (
            <Card
              key={business.business_id}
              data-testid={`business-card-${business.business_id}`}
              className={`bg-white dark:bg-slate-800 border-2 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-indigo-300 dark:hover:border-indigo-600 ${
                selecting ? "opacity-50 pointer-events-none" : ""
              }`}
              onClick={() => handleSelectBusiness(business)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Business Name */}
                    <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-2 truncate">
                      {business.name}
                    </h3>
                    
                    {/* Business Details */}
                    <div className="space-y-1.5">
                      {business.city && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <MapPin className="w-4 h-4 text-rose-500 flex-shrink-0" />
                          <span className="truncate">{business.city}</span>
                        </div>
                      )}
                      {business.industry && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <Briefcase className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                          <span className="truncate">{business.industry}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <Users className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span>{business.lead_count || 0} leads</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Arrow Icon */}
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-50 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
                    <ChevronRight className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                </div>
                
                {/* Status Badge */}
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    business.status === "active"
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                      : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
                  }`}>
                    {business.status || "active"}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-6">
          Logged in as <strong>{user?.name || user?.email}</strong>
        </p>
      </div>
    </div>
  );
};

export default BusinessSelectionModal;
